<template>
  <div class="flex flex-wrap mt-4">
    <div class="w-full mb-12 px-4">
      <card-record
        title="Empresa"
        resource="empresas"
        primaryKey="idempresa"
        :record="record"
        :columns="columns"
        @saveChanges="handleSave"
        :service="service"
      />
    </div>
    <div class="w-full mb-12 px-4" v-if="!isNew">
      <div
        class="text-sm font-medium text-center text-gray-500 border-b border-gray-200 mb-3"
      >
        <ul
          class="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200"
        >
          <li class="me-2">
            <button
              @click="openTab('logo')"
              class="inline-block p-4 rounded-t-lg"
              :class="[
                tabName === 'logo'
                  ? 'active text-blue-600 bg-gray-100'
                  : 'hover:text-gray-600 hover:bg-gray-50',
              ]"
            >
              Logo
            </button>
          </li>
          <li class="me-2">
            <button
              @click="openTab('obras')"
              class="inline-block p-4 rounded-t-lg"
              :class="[
                tabName === 'obras'
                  ? 'active text-blue-600 bg-gray-100'
                  : 'hover:text-gray-600 hover:bg-gray-50',
              ]"
            >
              {{ $t("Obras") }}
            </button>
          </li>
          <li class="me-2">
            <button
              @click="openTab('proyectos')"
              class="inline-block p-4 rounded-t-lg"
              :class="[
                tabName === 'proyectos'
                  ? 'active text-blue-600 bg-gray-100'
                  : 'hover:text-gray-600 hover:bg-gray-50',
              ]"
            >
              {{ $t("Presupuestos") }}
            </button>
          </li>
        </ul>
      </div>
      <logo-upload
        v-if="tabName === 'logo'"
        :idempresa="idempresa"
        :logoVersion="record.logo_version"
        @logoUpdated="onLogoUpdated"
      />
      <card-list
        title="Obras"
        resource="obras"
        primaryKey="idobra"
        :records="obras"
        :meta="obrasMeta"
        :columns="obrasColumns"
        :readonly="obrasReadonly"
        v-if="tabName === 'obras'"
      />
      <card-list
        title="Presupuestos"
        resource="proyectos"
        primaryKey="idpresupuesto"
        :records="proyectos"
        :meta="proyectosMeta"
        :columns="proyectosColumns"
        :readonly="proyectosReadonly"
        v-if="tabName === 'proyectos'"
      >
        <template v-slot:after-table>
          <totales-proyectos :meta="proyectosMeta" />
        </template>
      </card-list>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import CardList from "@/components/Cards/CardList.vue";
import CardRecord from "@/components/Cards/CardRecord.vue";
import LogoUpload from "@/components/Forms/LogoUpload.vue";
import TotalesProyectos from "@/components/Cards/TotalesProyectos.vue";
import EmpresaService from "@/services/empresa.service";
import ObraService from "@/services/obra.service";
import PresupuestoService from "@/services/presupuesto.service";
import { empresa } from "@/models/empresa";
import { obra } from "@/models/obra";
import { presupuesto } from "@/models/presupuesto";

export default {
  components: {
    CardList,
    CardRecord,
    LogoUpload,
    TotalesProyectos,
  },

  data() {
    return {
      record: {},
      columns: empresa,
      service: EmpresaService,
      obras: {},
      obrasMeta: {},
      obrasColumns: obra.filter(
        (column) => column.showOnList && column.name !== "idempresa"
      ),
      proyectos: {},
      proyectosMeta: {},
      proyectosColumns: presupuesto.filter(
        (column) => column.showOnList && column.name !== "idempresa"
      ),
      tabName: "logo",
    };
  },

  watch: {
    $route: {
      immediate: true,
      handler(/*to, from*/) {
        EmpresaService.get(this.idempresa)
          .then((data) => {
            this.record = data.record;
          })
          .catch((response) => {
            console.log(response);
          });

        this.loadTabData();
      },
    },
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
    }),

    idempresa() {
      return this.$route.params.id;
    },

    isNew() {
      return this.idempresa == "new";
    },

    obrasReadonly() {
      const roles = this.userInfo?.roles || [];
      return !roles.includes("admin") && !roles.includes("personal");
    },

    proyectosReadonly() {
      const roles = this.userInfo?.roles || [];
      return !roles.includes("admin") && !roles.includes("personal");
    },
  },

  methods: {
    handleSave(record) {
      this.record = record;
    },

    onLogoUpdated(newVersion) {
      this.record = { ...this.record, logo_version: newVersion };
    },

    openTab(newTabName) {
      this.$router.push({ path: this.$route.path })
        .catch(() => {})
        .then(() => {
          this.tabName = newTabName;
          this.loadTabData();
        });
    },

    loadTabData() {
      if (this.tabName === "obras") {
        this.loadObras();
      } else if (this.tabName === "proyectos") {
        this.loadPresupuestos();
      }
    },

    loadObras() {
      const params = { ...this.$route.query };
      const obraFilters = JSON.parse(params.filters || "{}");
      obraFilters.idempresa = { value: this.idempresa, type: "empresa" };
      delete obraFilters["obra.idempresa"];
      params.filters = JSON.stringify(obraFilters);

      ObraService.list(params)
        .then((data) => {
          this.obras = data.records;
          this.obrasMeta = data.meta;
        })
        .catch((response) => {
          console.log(response);
        });
    },

    loadPresupuestos() {
      const params = { ...this.$route.query };
      const presupuestoFilters = JSON.parse(params.filters || "{}");
      presupuestoFilters["obra.idempresa"] = {
        value: this.idempresa,
        type: "empresa",
      };
      delete presupuestoFilters.idempresa;
      params.filters = JSON.stringify(presupuestoFilters);

      PresupuestoService.list(params)
        .then((data) => {
          this.proyectos = data.records;
          this.proyectosMeta = data.meta;
        })
        .catch((response) => {
          console.log(response);
        });
    },
  },
};
</script>
